<template>
  <Accordion
    v-bind="$attrs"
    :data-sb-automation="$attrs['data-sb-automation'] || 'sb-accordion'"
    data-sb-regression="sb-accordion"
    :class="$style.flexAccordion"
    :style="`gap: ${gap}px;`"
  >
    <AccordionPanel
      v-for="(panel, index) in panelDefinitions"
      :key="index"
      :value="index"
      :class="$style.panel"
      :disabled="panel.disabled"
      data-sb-automation="sb-accordion-panel"
    >
      <AccordionHeader
        :class="panel.callToAction ? $style.callToAction : ''"
        data-sb-automation="sb-accordion-header"
      >
        <template v-if="typeof panel.title === 'string'">
          <sb-text data-sb-automation="sb-accordion-title">
            {{ panel.title }}
          </sb-text>
        </template>
        <template v-else>
          <component
            :is="panel.title"
            v-if="panel.title"
            data-sb-automation="sb-accordion-title"
          />
        </template>
        <template v-if="typeof panel.callToAction === 'string'">
          <sb-anchor
            data-sb-automation="sb-accordion-call-to-action"
            @click.stop="onActionClick({ index, panel })"
          >
            {{ panel.callToAction }}
          </sb-anchor>
        </template>
        <template v-else>
          <component
            :is="panel.callToAction"
            v-if="panel.callToAction"
            data-sb-automation="sb-accordion-call-to-action"
            @click.stop="onActionClick({ index, panel })"
          />
        </template>
      </AccordionHeader>
      <AccordionContent data-sb-automation="sb-accordion-content">
        <template v-if="typeof panel.content === 'string'">
          {{ panel.content }}
        </template>
        <template v-else>
          <component
            :is="panel.content"
            v-bind="panel.contentProps"
            v-if="panel.content"
          />
        </template>
      </AccordionContent>
    </AccordionPanel>
  </Accordion>
</template>

<script setup>
import { defineProps, defineEmits, useCssModule, defineOptions } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import SbAnchor from '@src/components/Anchor/SbAnchor.vue';
import SbText from '@src/components/Text/SbText.vue';

defineOptions({
  name: 'SbAccordion',
});

defineProps(
  {
    /**
     * An Array containing panel objects: Each panel object can have title (string or component), callToAction (string or component), content (string or component)
     */
    panelDefinitions: {
      type: Array,
      default: () => ([]),
    },
    /**
     * The gap between panels (in px).
     */
    gap: {
      type: Number,
      default: () => (10),
    },
  },
);

const emit = defineEmits([
  /**
   * Emitted when the action button is clicked.
   */
  'actionClick',
]);

const $style = useCssModule();

const onActionClick = ({ index, panel }) => {
  emit('actionClick', { index, panel });
};
</script>

<style lang="scss" module>
.panel {
  box-shadow: var(--p-accordion-panel-shadow);
  border-radius: var(--p-accordion-panel-border-radius);
  background: var(--color-white);
  overflow: hidden;
}

.flexAccordion {
  display: flex;
  flex-direction: column;
}

.callToAction :last-child {
  display: none;
}
</style>
